import { useQuery } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export const roomTypesSchemaQueryKeys = {
  all: ['accommodation', 'room-types-schema'] as const,
  details: () => [...roomTypesSchemaQueryKeys.all, 'detail'] as const,
  detail: (propertyId?: string) => [...roomTypesSchemaQueryKeys.details(), propertyId] as const,
};

interface SchemaProperty {
  enum?: Array<unknown>;
  items?: SchemaProperty;
  properties?: {
    type?: SchemaProperty;
  };
}

interface RoomTypesSchema {
  properties?: {
    bed_groups?: {
      items?: {
        properties?: {
          configuration?: {
            items?: SchemaProperty;
          };
        };
      };
    };
    category?: SchemaProperty;
  };
}

interface RoomTypesSchemaResponse {
  post?: {
    body?: {
      schema?: RoomTypesSchema;
    };
  };
  put?: {
    body?: {
      schema?: RoomTypesSchema;
    };
  };
}

interface UseRoomTypesSchemaParams {
  enabled?: boolean;
  propertyId?: string;
}

export function getRoomCategoryOptionsFromSchema(schema?: RoomTypesSchemaResponse) {
  return (
    schema?.post?.body?.schema?.properties?.category?.enum ??
    schema?.put?.body?.schema?.properties?.category?.enum ??
    []
  ).filter((category): category is string => typeof category === 'string');
}

export function getRoomBedTypeOptionsFromSchema(schema?: RoomTypesSchemaResponse) {
  return getSchemaStringEnum(
    getRoomBedConfigurationItemsSchema(schema?.post?.body?.schema) ??
      getRoomBedConfigurationItemsSchema(schema?.put?.body?.schema),
  );
}

export function useRoomTypesSchema({ enabled = true, propertyId }: UseRoomTypesSchemaParams) {
  return useQuery<RoomTypesSchemaResponse>({
    queryKey: roomTypesSchemaQueryKeys.detail(propertyId),
    queryFn: async () => (await ReservationService.getRoomTypesSchema(propertyId)) as RoomTypesSchemaResponse,
    enabled: enabled && !!propertyId,
  });
}

function getRoomBedConfigurationItemsSchema(schema?: RoomTypesSchema) {
  return schema?.properties?.bed_groups?.items?.properties?.configuration?.items;
}

function getSchemaStringEnum(schema?: SchemaProperty) {
  return (schema?.properties?.type?.enum ?? schema?.enum ?? []).filter(
    (option): option is string => typeof option === 'string',
  );
}
